import { useCallback, useEffect, useRef, useState } from "react";

export function useSlideNavigation(slideCount: number, enabled = true) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const isTransitioning = useRef(false);
  const lastWheelTime = useRef(0);

  const goTo = useCallback(
    (index: number) => {
      if (isTransitioning.current) return;
      const clamped = Math.max(0, Math.min(slideCount - 1, index));
      if (clamped === currentSlide) return;
      isTransitioning.current = true;
      setCurrentSlide(clamped);
      setTimeout(() => {
        isTransitioning.current = false;
      }, 900);
    },
    [currentSlide, slideCount]
  );

  const next = useCallback(() => goTo(currentSlide + 1), [goTo, currentSlide]);
  const prev = useCallback(() => goTo(currentSlide - 1), [goTo, currentSlide]);

  useEffect(() => {
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const now = Date.now();
      if (now - lastWheelTime.current < 800) return;
      lastWheelTime.current = now;
      if (e.deltaY > 0) next();
      else if (e.deltaY < 0) prev();
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowDown" || e.key === " ") {
        e.preventDefault();
        next();
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        prev();
      }
    };

    let touchStartY = 0;
    const onTouchStart = (e: TouchEvent) => {
      touchStartY = e.touches[0]?.clientY ?? 0;
    };
    const onTouchEnd = (e: TouchEvent) => {
      const deltaY = touchStartY - (e.changedTouches[0]?.clientY ?? 0);
      if (Math.abs(deltaY) > 50) {
        if (deltaY > 0) next();
        else prev();
      }
    };

    if (!enabled) return;

    window.addEventListener("wheel", onWheel, { passive: false });
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchend", onTouchEnd, { passive: true });

    return () => {
      window.removeEventListener("wheel", onWheel);
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchend", onTouchEnd);
    };
  }, [next, prev, enabled]);

  return { currentSlide, goTo, next, prev };
}
